'use client';

import { useEffect, useRef } from 'react';
import { Download, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LocaleLink } from '@/components/locale-link';

export interface CreditsUsedSnapshot {
  charged: number;
  balance: number | null;
  /** Free because the user already owns this exact copy. */
  entitled?: boolean;
  /** Show the "it's in your Downloads" pointer — document actions only. */
  document?: boolean;
}

/**
 * Validate a billing snapshot coming back from a charge response. Returns
 * null when the numbers are missing, or when nothing happened worth saying —
 * billing is best-effort server-side, and a popup with made-up numbers is
 * worse than no popup.
 *
 * A charge of 0 is *not* automatically silence: when the server says the
 * user already owned the document, that is the whole point of pay-once and
 * they should see it working. Zero without that flag stays silent, because
 * it also describes a disabled billing config.
 */
export function creditsUsedSnapshot(
  charged: number | null | undefined,
  balance: number | null | undefined,
  opts: { entitled?: boolean; document?: boolean } = {},
): CreditsUsedSnapshot | null {
  if (
    typeof charged !== 'number' ||
    !Number.isFinite(charged) ||
    charged < 0 ||
    typeof balance !== 'number' ||
    !Number.isFinite(balance)
  ) {
    return null;
  }
  if (charged === 0 && !opts.entitled) return null;
  return {
    charged,
    balance,
    entitled: opts.entitled === true,
    document: opts.document === true,
  };
}

// Brief confirmation — long enough to read the numbers, short enough not to
// feel like a notification demanding attention. The variant carrying a link
// gets longer: a link nobody has time to click is worse than no link.
const AUTO_DISMISS_MS = 1500;
const AUTO_DISMISS_WITH_LINK_MS = 5000;

/**
 * Toast-styled confirmation at the top center: what an action cost and what
 * is left. Shared by every web action that debits credits (Bilanz view /
 * HTML export, register-file download).
 *
 * Three things it says, in decreasing frequency:
 * - "10 credits used, 240 left" — the normal charge.
 * - "Free — you already have this document" — the pay-once grace actually
 *   working, which was until now completely invisible.
 * - "Find it any time in Downloads" — where the document went, for the
 *   people who don't yet know that page exists.
 *
 * Non-modal: the wrapper ignores pointer events so it never blocks a click,
 * while the card itself accepts them so the link is usable.
 */
export function CreditsUsedPopup({
  snapshot,
  onClose,
}: {
  snapshot: CreditsUsedSnapshot | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Callers pass `onClose` inline; keep the latest one in a ref so parent
  // re-renders don't restart the dismiss timer.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    if (!snapshot) return;
    const timer = setTimeout(
      () => onCloseRef.current(),
      snapshot.document ? AUTO_DISMISS_WITH_LINK_MS : AUTO_DISMISS_MS,
    );
    return () => clearTimeout(timer);
  }, [snapshot]);

  if (!snapshot) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 lg:left-[var(--app-sidebar-width,0px)] top-4 z-[100] flex justify-center">
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-auto flex max-w-[92vw] items-start gap-3 rounded-lg border border-border bg-popover p-4 text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95 duration-200"
      >
        {snapshot.entitled ? (
          <Download className="mt-0.5 size-4 shrink-0" aria-hidden />
        ) : (
          <Wallet className="mt-0.5 size-4 shrink-0" aria-hidden />
        )}
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-medium">
            {snapshot.entitled
              ? t(
                  'credits.usedPopup.freeAlreadyOwned',
                  'Free — you already have this document',
                )
              : t('credits.usedPopup.title', { used: snapshot.charged })}
          </p>
          {snapshot.balance !== null && <p className="text-xs text-muted-foreground">
            {t('credits.usedPopup.remaining', { left: snapshot.balance })}
          </p>}
          {snapshot.document && (
            <p className="mt-1 text-xs text-muted-foreground">
              <LocaleLink
                href="/activity?tab=downloads"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={() => onCloseRef.current()}
              >
                {t(
                  'credits.usedPopup.findInDownloads',
                  'Find it any time in Downloads',
                )}
              </LocaleLink>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
